import React, { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Trash2, ImagePlus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { milestoneService, formatApiError, resolveImageUrl } from "../../services/catalogService";

const EMPTY = { year: new Date().getFullYear(), title_en: "", text_en: "", title_ru: "", text_ru: "", sort_order: 0 };

// About Us timeline manager (client observations 27-oct).
// NOTE for the team: seeded milestones are SAMPLES — replace with TESSA's real history.
export default function MilestonesPanel() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null); // null = closed; {id?} editing
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [imageFor, setImageFor] = useState(null);

  const load = () => milestoneService.list().then(setItems).catch((e) => toast.error("Could not load milestones", { description: formatApiError(e) }));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        year: Number(form.year),
        title_en: form.title_en, text_en: form.text_en,
        title_ru: form.title_ru, text_ru: form.text_ru,
        sort_order: Number(form.sort_order) || 0,
      };
      if (form.id) await milestoneService.update(form.id, payload);
      else await milestoneService.create(payload);
      toast.success(form.id ? "Milestone updated" : "Milestone created");
      setForm(null);
      load();
    } catch (e) {
      toast.error("Could not save milestone", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await milestoneService.remove(id);
      toast.success("Milestone deleted");
      load();
    } catch (e) {
      toast.error("Could not delete milestone", { description: formatApiError(e) });
    }
  };

  const onImageFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !imageFor) return;
    try {
      await milestoneService.uploadImage(imageFor, file);
      toast.success("Image uploaded");
      load();
    } catch (err) {
      toast.error("Image upload failed", { description: formatApiError(err) });
    } finally {
      setImageFor(null);
    }
  };

  if (items === null) return <div className="text-[#817B74] py-20 text-center"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto" data-testid="admin-about-panel">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl text-[#252525]">About — milestones</h1>
          <p className="text-[#817B74] text-sm mt-1">
            Timeline of the public "About us" section. The seeded entries are samples — replace them with TESSA's real history.
          </p>
        </div>
        <button onClick={() => setForm({ ...EMPTY })} data-testid="milestone-new"
          className="inline-flex items-center gap-2 h-10 px-4 bg-[#D92A47] text-white text-[11px] uppercase tracking-[0.14em] hover:bg-[#b71e3a]">
          <Plus size={14} /> Add milestone
        </button>
      </div>

      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onImageFile} data-testid="milestone-image-input" />

      {form && (
        <div className="bg-white border border-black/10 p-6 mb-8 space-y-4" data-testid="milestone-form">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-[#817B74] block mb-1">Year *</label>
              <input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="milestone-year"
                className="w-full border-b border-black/15 bg-transparent py-2 outline-none focus:border-[#D92A47]" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-[#817B74] block mb-1">Order</label>
              <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} data-testid="milestone-order"
                className="w-full border-b border-black/15 bg-transparent py-2 outline-none focus:border-[#D92A47]" />
            </div>
          </div>
          {[["title_en", "Title (EN) *"], ["text_en", "Text (EN)"], ["title_ru", "Title (RU)"], ["text_ru", "Text (RU)"]].map(([key, label]) => (
            <div key={key}>
              <label className="text-[11px] uppercase tracking-[0.14em] text-[#817B74] block mb-1">{label}</label>
              {key.startsWith("text") ? (
                <textarea rows={2} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`milestone-${key}`}
                  className="w-full border-b border-black/15 bg-transparent py-2 outline-none focus:border-[#D92A47] resize-none" />
              ) : (
                <input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`milestone-${key}`}
                  className="w-full border-b border-black/15 bg-transparent py-2 outline-none focus:border-[#D92A47]" />
              )}
            </div>
          ))}
          <div className="flex gap-3 pt-2">
            <button onClick={save} disabled={busy} data-testid="milestone-save"
              className="inline-flex items-center gap-2 h-10 px-5 bg-[#252525] text-white text-[11px] uppercase tracking-[0.14em] hover:bg-[#D92A47] disabled:opacity-50">
              {busy && <Loader2 size={14} className="animate-spin" />} Save
            </button>
            <button onClick={() => setForm(null)} data-testid="milestone-cancel"
              className="h-10 px-5 border border-black/15 text-[11px] uppercase tracking-[0.14em] hover:border-[#D92A47]">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-black/10">
        {items.length === 0 ? (
          <p className="p-8 text-sm text-[#817B74]">No milestones yet — the About section stays hidden until you add one.</p>
        ) : (
          <ul className="divide-y divide-black/5">
            {items.map((m) => (
              <li key={m.id} className="flex items-center gap-4 px-5 py-4" data-testid={`milestone-row-${m.id}`}>
                <span className="font-serif text-2xl text-[#D92A47] w-20 shrink-0">{m.year}</span>
                {m.image ? (
                  <img src={resolveImageUrl(m.image)} alt="" className="h-10 w-14 object-cover shrink-0" />
                ) : (
                  <span className="h-10 w-14 bg-[#F6F2EC] shrink-0 grid place-items-center text-[10px] text-[#817B74]">no img</span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[#252525] truncate">{m.titleEn}</div>
                  <div className="text-xs text-[#817B74] truncate">{m.textEn}</div>
                </div>
                <button onClick={() => { setImageFor(m.id); fileRef.current?.click(); }} data-testid={`milestone-image-${m.id}`}
                  title="Upload image" className="p-2 text-[#817B74] hover:text-[#D92A47]">
                  <ImagePlus size={16} />
                </button>
                <button onClick={() => setForm({ id: m.id, year: m.year, title_en: m.titleEn, text_en: m.textEn, title_ru: m.titleRu, text_ru: m.textRu, sort_order: m.sortOrder })}
                  data-testid={`milestone-edit-${m.id}`} title="Edit" className="p-2 text-[#817B74] hover:text-[#252525]">
                  <Pencil size={16} />
                </button>
                <button onClick={() => remove(m.id)} data-testid={`milestone-delete-${m.id}`} title="Delete" className="p-2 text-[#817B74] hover:text-[#D92A47]">
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
